import { SortingState } from '@tanstack/react-table';
import { ExpectedQueryData } from 'components/table/hooks/useServerFilteredTable';
import { SortDirection } from 'components/table/Table.types';
import { getSortParam } from 'components/table/utils/sorting';
import {
  GetInternalIntegrationSettingsResponseTO,
  IntegrationCategory,
  IntegrationType,
  InternalIntegrationSetting,
} from 'models/InternalIntegrationSetting';
import { IntegrationsBadgeFilter } from 'pages/integrations/IntegrationsTabs.constants';
import API from 'services/API';

export const INTEGRATION_SETTINGS_SORTING: {
  [K: string]: string;
} = {
  actions: 'SORT_BY_INTEGRATION_STATUS',
  name: 'SORT_BY_INTEGRATION_NAME',
};

export const INTERNAL_INTEGRATION_SETTINGS_BASE_QUERY_KEY =
  'internal-integration-settings';

export const getIntegrationSettingsListQueryKey = ({
  type,
  category,
  sorting,
  badgeFilter,
}: {
  type: IntegrationType;
  category?: IntegrationCategory | null;
  sorting?: SortingState;
  badgeFilter?: string;
}) => [
  INTERNAL_INTEGRATION_SETTINGS_BASE_QUERY_KEY,
  'list',
  {
    type,
    category: category ?? 'no-category',
    sorting,
    badgeFilter: badgeFilter ?? 'no-with-badges',
  },
];

type QueryReturnType = ExpectedQueryData<InternalIntegrationSetting>;

export interface IntegrationSettingsQueryOptions {
  sorting?: SortingState;
  type: IntegrationType;
  category?: IntegrationCategory;
  defaultSorting?: {
    sort: string;
    direction: SortDirection;
  };
  badgeFilter?: IntegrationsBadgeFilter;
}

/**
 * Fetches the integration settings list from API
 */
export const integrationsSettingsQuery = ({
  sorting = [],
  defaultSorting,
  type,
  category,
  badgeFilter,
}: IntegrationSettingsQueryOptions) => ({
  queryKey: getIntegrationSettingsListQueryKey({
    type,
    category,
    sorting: sorting ?? defaultSorting,
    badgeFilter,
  }),
  queryFn: async (): Promise<QueryReturnType> => {
    const sort =
      getSortParam(sorting, INTEGRATION_SETTINGS_SORTING) ?? defaultSorting;

    const response = await API.get<GetInternalIntegrationSettingsResponseTO>(
      '/api/internal/integrations/integration-settings',
      {
        params: {
          type,
          category,
          ...(badgeFilter ? { badgeFilter } : {}),
          ...(sort ? { sort: sort.sort, sortDirection: sort.direction } : {}),
        },
      },
    );

    const { integrationSettings } = response.data;

    return {
      data: integrationSettings,
      _metadata: {
        totalCount: integrationSettings.length,
        _count: integrationSettings.length,
        _totalCount: integrationSettings.length,
      },
    };
  },
  enabled: true,
  cacheTime: 5 * 60 * 1000,
  staleTime: 30000,
  keepPreviousData: false,
});
